import { MenuItem, Tooltip } from '@blueprintjs/core';
import {
  loan_application_lender_tier_hooks,
  lock_hooks,
  useApplicationDetail,
  useApplicationStatusUtils,
  useArchiveApplication,
  useLoanQuotes,
  useLoanRequests,
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import { useConfirmDialog } from '../../../hooks/utils/useConfirmDialog';
import {
  loan_application,
  event_log,
  loan_quote,
  loan_request,
} from '@axo/shared/data-access/types';
import styles from './CustomerWithdrawnButton.module.css';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { EventCodeACE } from '../../EventLogWrapper/event-code';

export function CustomerWithdrawnButton({
  applicationID,
}: {
  applicationID: string;
}) {
  const archiveApplication = useArchiveApplication(applicationID);
  const { confirm } = useConfirmDialog();
  const app = useApplicationDetail(applicationID);
  const { isFinalApplicationStatus } = useApplicationStatusUtils();
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    app.data?.MarketCountry
  ).data;
  const quotes = useLoanQuotes(applicationID);
  const { isInQuoteStatusGroup } = useQuoteStatusUtils();
  const requests = useLoanRequests({ loanApplicationID: applicationID });
  const { isInRequestStatusGroup } = useRequestStatusUtils();
  const lenderTier =
    loan_application_lender_tier_hooks.useLenderTier(applicationID);
  const appTag: event_log.Resource = {
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  };
  const log = useEventLogger(appTag);

  const handleArchive = () => {
    confirm({
      message:
        'Are you sure you want to withdraw this application for the customer?',
      confirmText: 'Withdrawn by customer',
      cancelText: 'Cancel',
      confirmCallback: async () => {
        await archiveApplication.mutateAsync(
          loan_application.ArchiveRequestType.WithdrawnByCustomer
        );
        log(EventCodeACE.WithdrawnByCustomer);
      },
    });
  };

  const isFinalStatus = app.data?.Status
    ? isFinalApplicationStatus(app.data.Status)
    : false;

  const hasActiveLoanQuotes = quotes.data?.some((quote) =>
    isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Active)
  );

  const isPendingResponseFromBank = requests.data?.some((request) =>
    isInRequestStatusGroup(
      request.Status,
      loan_request.RequestStatusGroup.PendingRegistration
    )
  );

  const tierRegistrationOngoing = lenderTier.data
    ? lenderTier.data.Ongoing
    : false;

  function canWithdraw() {
    if (!hasLock) {
      return {
        disabled: true,
        text: 'You can only edit the application when you have locked it.',
      };
    }
    if (isFinalStatus) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when in a final state.',
      };
    }
    if (hasActiveLoanQuotes) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when there are active loan quotes.',
      };
    }
    if (isPendingResponseFromBank) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when pending a response from a bank.',
      };
    }
    if (tierRegistrationOngoing) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when the tier registration is ongoing.',
      };
    }
    return {
      disabled: false,
      text: '',
    };
  }

  return (
    <Tooltip
      disabled={!canWithdraw().disabled}
      placement={'left'}
      className={styles.customerWithdrawnTooltip}
      content={<span>{canWithdraw().text}</span>}
      openOnTargetFocus={false}
    >
      <MenuItem
        icon="walk"
        onClick={handleArchive}
        disabled={canWithdraw().disabled}
        text="Customer withdrawn"
      />
    </Tooltip>
  );
}
